import { consumeOneTimeNotification } from './oneTimeNotification';

export const SESSION_EXPIRY_MESSAGE = 'Сесію завершено. Увійдіть знову.';
export const SESSION_EXPIRY_STORAGE_KEY = 'foxy.session-expiry.notification';
export const SESSION_EXPIRY_LOGIN_PATH = '/login';

const INSTALLATION_MARKER = Symbol.for('foxy.sessionExpiry.interceptorInstalled');
const SHARED_STATE_MARKER = Symbol.for('foxy.sessionExpiry.sharedState');

function getRuntimeLocation(runtime) {
    const href = runtime?.location?.href;

    if (typeof href === 'string' && href !== '') {
        return href;
    }

    const origin = runtime?.location?.origin;
    return typeof origin === 'string' && origin !== '' ? `${origin}/` : null;
}

function isSameOriginRequest(error, runtime) {
    const pageLocation = getRuntimeLocation(runtime);
    const config = error?.config || error?.response?.config || {};
    const requestUrl = config.url || error?.request?.responseURL;

    if (!pageLocation || typeof requestUrl !== 'string' || requestUrl === '') {
        return false;
    }

    try {
        const pageUrl = new URL(pageLocation);
        const baseUrl = typeof config.baseURL === 'string' && config.baseURL !== ''
            ? new URL(config.baseURL, pageUrl)
            : pageUrl;

        return new URL(requestUrl, baseUrl).origin === pageUrl.origin;
    } catch {
        return false;
    }
}

function getSessionStorage(runtime) {
    try {
        return runtime?.sessionStorage || null;
    } catch {
        return null;
    }
}

function queueSessionExpiryNotification(runtime) {
    const storage = getSessionStorage(runtime);

    if (!storage || typeof storage.setItem !== 'function') {
        return false;
    }

    try {
        storage.setItem(SESSION_EXPIRY_STORAGE_KEY, SESSION_EXPIRY_MESSAGE);
        return true;
    } catch {
        return false;
    }
}

function clearStaleAuthenticatedState(runtime, documentObject) {
    try {
        runtime.isAuthenticated = false;
    } catch {
        // Hard navigation remains authoritative if a host marker is read-only.
    }

    try {
        const bootstrap = runtime.__ACCOUNT_BOOTSTRAP__;
        if (bootstrap && typeof bootstrap === 'object') {
            runtime.__ACCOUNT_BOOTSTRAP__ = { ...bootstrap, viewer: null };
        }
    } catch {
        // The Account root is still quarantined below.
    }

    const accountRoot = documentObject?.getElementById?.('account-app');
    if (!accountRoot) {
        return;
    }

    accountRoot.setAttribute('inert', '');
    accountRoot.setAttribute('aria-hidden', 'true');

    if (accountRoot.style) {
        accountRoot.style.pointerEvents = 'none';
        accountRoot.style.visibility = 'hidden';
    }
}

function getSharedState(runtime) {
    if (!runtime[SHARED_STATE_MARKER]) {
        Object.defineProperty(runtime, SHARED_STATE_MARKER, {
            configurable: true,
            value: {
                handled: false,
                navigationBarrier: null,
                navigationFailure: null,
            },
        });
    }

    return runtime[SHARED_STATE_MARKER];
}

function getNavigationBarrier(state) {
    if (!state.navigationBarrier) {
        // Component catch handlers must not render the rejected backend payload
        // while the browsing context is being replaced.
        state.navigationBarrier = new Promise(() => {});
    }

    return state.navigationBarrier;
}

function createNavigationFailure(cause) {
    const error = new Error(SESSION_EXPIRY_MESSAGE);
    error.code = 'FOXY_SESSION_EXPIRY_NAVIGATION_FAILED';
    error.sessionExpiryHandled = true;
    error.cause = cause;

    return error;
}

function defaultNavigate(runtime, target) {
    if (!runtime?.location || typeof runtime.location.replace !== 'function') {
        throw new Error('Hard navigation is unavailable.');
    }

    runtime.location.replace(target);
}

export function installSessionExpiryInterceptor(axiosInstance, options = {}) {
    if (!axiosInstance?.interceptors?.response?.use || axiosInstance[INSTALLATION_MARKER]) {
        return false;
    }

    Object.defineProperty(axiosInstance, INSTALLATION_MARKER, {
        configurable: true,
        value: true,
    });

    const runtime = options.runtime || window;
    const documentObject = options.document
        || runtime?.document
        || (typeof document !== 'undefined' ? document : null);
    const startedAuthenticated = options.startedAuthenticated === undefined
        ? Boolean(runtime?.isAuthenticated)
        : Boolean(options.startedAuthenticated);
    const navigate = options.navigate || ((target) => defaultNavigate(runtime, target));

    axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            const isAuthoritativeExpiry = startedAuthenticated
                && error?.response?.status === 401
                && isSameOriginRequest(error, runtime);

            if (!isAuthoritativeExpiry) {
                return Promise.reject(error);
            }

            const state = getSharedState(runtime);

            if (!state.handled) {
                state.handled = true;
                queueSessionExpiryNotification(runtime);
                clearStaleAuthenticatedState(runtime, documentObject);

                try {
                    navigate(SESSION_EXPIRY_LOGIN_PATH);
                } catch (navigationError) {
                    state.navigationFailure = createNavigationFailure(navigationError);
                }
            }

            if (state.navigationFailure) {
                return Promise.reject(state.navigationFailure);
            }

            return getNavigationBarrier(state);
        }
    );

    return true;
}

export function consumeSessionExpiryNotification(runtime, present) {
    if (typeof present !== 'function') {
        return false;
    }

    const storage = getSessionStorage(runtime);
    if (!storage || typeof storage.getItem !== 'function' || typeof storage.removeItem !== 'function') {
        return false;
    }

    let message;

    try {
        message = storage.getItem(SESSION_EXPIRY_STORAGE_KEY);
        if (typeof message !== 'string' || message.trim() === '') {
            return false;
        }

        storage.removeItem(SESSION_EXPIRY_STORAGE_KEY);
    } catch {
        return false;
    }

    return consumeOneTimeNotification(
        { sessionExpiry: message },
        'sessionExpiry',
        present
    );
}
